import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { StateCard, Pill } from '../../components/StateCard';
import { Empty, GameArt, GameTile, Period, Section, eventStateLabel, formatDate, relativeDate } from '../../components/site';
import type { GameCard } from '../../components/site';
import type { ApiEnvelope } from '../../lib/types';

interface NewsItem {
  id: string; slug: string; kind: string; title: string; excerpt: string;
  pinned: boolean; severity: string | null; game_id: string | null; published_at: string | null;
}

interface EventItem {
  slug: string; name: string; kind: string; state: string; game_id: string | null;
  starts_at: string | null; ends_at: string | null; claim_deadline: string | null;
}

interface HomePayload {
  featured: GameCard[];
  new_releases: GameCard[];
  recently_updated: GameCard[];
  coming_soon: GameCard[];
  by_platform: Record<string, GameCard[]>;
  news: NewsItem[];
  events: EventItem[];
  open_incidents: { reference: string; title: string; severity: string; state: string }[];
  counts: { live: number; coming_soon: number; platforms: number };
}

const PLATFORM_ROUTE: Record<string, string> = {
  ios: '/mobile', android: '/mobile', roblox: '/roblox', steam: '/pc', pc: '/pc', web: '/games',
};

const PLATFORM_TITLE: Record<string, string> = {
  ios: 'iOS', android: 'Android', roblox: 'Roblox', steam: 'Steam', pc: 'PC', web: 'Web',
};

/**
 * P001 — service home.
 *
 * A game hub, not a corporate landing page: the games carry the visual weight and
 * every number shown is one the server actually counts.
 */
export function PublicHome() {
  const home = useResource<HomePayload>(
    (signal) => api.public('/api/v1/site/home', { signal }) as Promise<ApiEnvelope<HomePayload>>,
    [],
    { emptyWhen: (data) => !data },
  );

  const data = home.data;
  const hasCounts = data ? data.counts.live + data.counts.coming_soon + data.counts.platforms > 0 : false;
  // The collage is a 2x2 grid, so three tiles would leave a visible hole: take
  // four when there are four, otherwise two, otherwise none.
  const heroPool = data
    ? [...data.featured, ...data.new_releases, ...data.recently_updated, ...data.coming_soon]
        .filter((game, index, list) => list.findIndex((g) => g.id === game.id) === index)
    : [];
  const heroGames = heroPool.length >= 4 ? heroPool.slice(0, 4) : heroPool.slice(0, 2);

  return (
    <>
      <section className="hero hero--solo" aria-labelledby="games-home-title">
        <div>
          <h1 id="games-home-title" className="hero__title">ASKRS Games</h1>
          <p className="hero__lead">ASKRS가 운영하는 게임 정보·소식·고객지원 웹사이트.</p>
          <div className="hero__actions">
            <Link className="btn btn--primary" to="/games">게임 보기</Link>
            <Link className="btn" to="/news">소식</Link>
          </div>
        </div>
      </section>
    <StateCard state={home.state} error={home.error} onRetry={home.reload}>
      {data && (
        <>
          {data.open_incidents.length > 0 && (
            <div className="banner" role="status">
              <span aria-hidden="true">⚠</span>
              <div>
                <strong>서비스 장애 발생</strong>{' '}
                {data.open_incidents.map((incident) => (
                  <Link key={incident.reference} to={`/status/incidents/${incident.reference}`} style={{ marginRight: 12 }}>
                    {incident.title}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {(heroGames.length > 0 || hasCounts) ? (
          <section className={heroGames.length > 0 ? 'hero' : 'hero hero--solo'}>
            <div>
              <p className="hero__lead">
                {heroGames.length > 0 ? '공개된 게임과 새로운 소식을 만나 보세요.' : '현재 공개된 게임 없음. 소식·고객지원·서비스 상태 확인 가능.'}
              </p>
              {/* A row of zeros says nothing, so it appears once there is something
                  to count. `hidden` alone would lose to the flex rule. */}
              {hasCounts && (
                <div className="hero__facts">
                  <div>
                    <div className="hero__fact-value">{data.counts.live}</div>
                    <div className="hero__fact-label">서비스 중</div>
                  </div>
                  <div>
                    <div className="hero__fact-value">{data.counts.platforms}</div>
                    <div className="hero__fact-label">플랫폼</div>
                  </div>
                  <div>
                    <div className="hero__fact-value">{data.counts.coming_soon}</div>
                    <div className="hero__fact-label">출시 예정</div>
                  </div>
                </div>
              )}
            </div>

            {heroGames.length > 0 && (
              <div className="hero__art" aria-hidden="true">
                {heroGames.map((game) => <GameArt key={game.id} game={game} className="tile" />)}
              </div>
            )}
          </section>
          ) : (
            <p className="section__desc">현재 공개된 게임 없음. 소식·고객지원·서비스 상태 확인 가능.</p>
          )}

          {data.featured.length > 0 && (
            <Section title="추천" description="지금 시작하기 좋은 작품" moreHref="/games">
              <div className="card-grid">
                {data.featured.map((game) => <GameTile key={game.id} game={game} />)}
              </div>
            </Section>
          )}

          {data.new_releases.length > 0 && (
            <Section title="신작" moreHref="/games">
              <div className="card-grid">
                {data.new_releases.map((game) => <GameTile key={game.id} game={game} />)}
              </div>
            </Section>
          )}

          {Object.keys(data.by_platform).length > 0 && (
            <Section title="플랫폼">
              <div className="grid grid--4">
                {Object.entries(data.by_platform).map(([platform, games]) => (
                  <Link key={platform} to={PLATFORM_ROUTE[platform] ?? '/games'} className="card" style={{ color: 'inherit' }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{PLATFORM_TITLE[platform] ?? platform}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{games.length}편</div>
                  </Link>
                ))}
              </div>
            </Section>
          )}

          {(data.news.length > 0 || data.events.length > 0) && (
            <div className="grid grid--2" style={{ alignItems: 'start' }}>
            <Section title="소식" moreHref="/news">
              {data.news.length === 0 ? (
                <Empty title="아직 소식이 없어요" body="새 소식이 공개되면 이곳에서 확인할 수 있어요." />
              ) : (
                <div className="row-list">
                  {data.news.map((item) => (
                    <Link key={item.id} to={`/news/${item.slug}`} className="row">
                      <div className="row__main">
                        <h3 className="row__title">
                          {item.pinned && <span style={{ color: 'var(--ak-orange-600)', marginRight: 6 }}>고정</span>}
                          {item.title}
                        </h3>
                        <p className="row__excerpt">{item.excerpt}</p>
                      </div>
                      <div className="row__aside">
                        <Pill tone={item.kind === 'patch_note' ? 'info' : 'neutral'}>
                          {item.kind === 'patch_note' ? '패치노트' : '공지'}
                        </Pill>
                        <span>{relativeDate(item.published_at)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </Section>

            <Section title="진행 중인 이벤트" moreHref="/events">
              {data.events.length === 0 ? (
                <Empty title="진행 중인 이벤트가 없어요" body="전체 이벤트에서 예정된 일정을 확인해 보세요." />
              ) : (
                <div className="grid" style={{ gap: 12 }}>
                  {data.events.map((event) => {
                    const state = eventStateLabel(event.state);
                    return (
                      <Link key={event.slug} to={`/events/${event.slug}`} className="event-card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                          <h3 className="event-card__title">{event.name}</h3>
                          <Pill tone={state.tone}>{state.label}</Pill>
                        </div>
                        <Period startsAt={event.starts_at} endsAt={event.ends_at} />
                        {event.claim_deadline && (
                          <p className="event-card__reward">수령 마감 {formatDate(event.claim_deadline)}</p>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </Section>
            </div>
          )}

          <Section title="도움말">
            <div className="card-grid">
              <Link to="/support" className="card" style={{ color: 'inherit' }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>도움말·문의</div>
                <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>자주 묻는 질문과 문의</div>
              </Link>
              <Link to="/status" className="card" style={{ color: 'inherit' }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>서비스 상태</div>
                <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>점검·장애 현황</div>
              </Link>
              <Link to="/privacy/requests" className="card" style={{ color: 'inherit' }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>내 데이터</div>
                <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>데이터 확인·삭제 요청</div>
              </Link>
            </div>
          </Section>
        </>
      )}
    </StateCard>
    </>
  );
}
